import { Platform } from '@angular/cdk/platform';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { DacklBreakpoint, DacklBreakpointObserver, DacklEmptyStateComponent } from '@datev/ngx-dackl';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  Breakpoint,
  Download,
  IDownloadDatasource,
  isProduct,
} from '../../model';
import { ObserveInput } from '../../shared/misc/observe-input.decorator';
import { RouteLocatorService } from '../../shared/services/route-locator.service';
import { NgTemplateOutlet, NgClass, AsyncPipe, DatePipe } from '@angular/common';
import { LoaderComponent } from '../loader/loader.component';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatChip } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { InternalServerErrorComponent } from '../internal-server-error/internal-server-error.component';
import { PushPipe } from '@ngrx/component';

type SupportedColumns =
  | 'name'
  | 'version'
  | 'published_date'
  | 'productGroupLabel'
  | 'more';

@Component({
    selector: 'dp-downloads-table',
    templateUrl: './downloads-table.component.html',
    styleUrls: ['./downloads-table.component.scss'],
    imports: [
    NgTemplateOutlet,
    LoaderComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    NgClass,
    MatChip,
    MatIcon,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatButton,
    DacklEmptyStateComponent,
    InternalServerErrorComponent,
    AsyncPipe,
    DatePipe,
    PushPipe
],
})
export class DownloadsTableComponent implements OnDestroy, AfterViewInit {
  private static instanceCounter = 0;
  private readonly instanceId = ++DownloadsTableComponent.instanceCounter;

  public readonly listToTableBreakpoint: Breakpoint = 'md';

  public readonly largeDisplayTableCellClass = `hidden ${this.listToTableBreakpoint}:block min-h-12 content-center`;
  public readonly smallDisplayTableCellClass = `${this.listToTableBreakpoint}:hidden`;

  public readonly isLargeScreen$ = this.breakpointObserver.breakpoint$.pipe(
    map(breakpoint => breakpoint >= DacklBreakpoint.LG),
  );

  @Input()
  public displayedColumns: Array<SupportedColumns> = [
    'name',
    'version',
    'published_date',
    'productGroupLabel',
  ];

  @ObserveInput('displayedColumns')
  public displayedColumnsInput$!: Observable<Array<SupportedColumns>>;

  @Input()
  public sortColumnOnSmallScreen: SupportedColumns = 'published_date';

  @ObserveInput('sortColumnOnSmallScreen')
  public sortColumnOnSmallScreenInput$!: Observable<SupportedColumns>;

  @Input()
  public selectedDownload: Download | undefined;

  @Input()
  public showLoadMoreButton: boolean = false;

  @Input()
  public dataSource!: IDownloadDatasource<any>;

  @Input()
  public tableAriaLabel: string = 'Downloads Tabelle';

  @Output()
  public readonly onRowSelection = new EventEmitter<Download>();

  @Output()
  public readonly onLoadMoreButtonClick = new EventEmitter<undefined>();

  @Output()
  public readonly onErrorReloadButtonClick = new EventEmitter<undefined>();

  @ViewChildren('rowFocus')
  public rowFocusElements!: QueryList<ElementRef<HTMLElement>>;

  /** Index of the row that currently holds the roving tabindex (tabindex=0). */
  public activeRowIndex = 0;

  @ViewChild(MatSort)
  set matSort(sort: MatSort) {
    // this needs to be a setter to ensure sort is added AFTER it is defined in the template, otherwise it won't work
    if (sort) {
      sort.sortChange
        .pipe(takeUntil(this.destroySubj.asObservable()))
        .subscribe(sort => {
          this.dataSource.updateSorting(sort);
        });
    }
  }

  public selectDownload$ = new Subject<Download>();

  private destroySubj = new Subject<boolean>();

  public isTableDisplay$ = this.breakpointObserver.breakpoint$.pipe(
    map(
      breakpoint =>
        breakpoint >=
        DacklBreakpoint[
          this.listToTableBreakpoint.toUpperCase() as 'LG' | 'MD' | 'SM' | 'XL'
        ],
    ),
  );

  private showRowMoreButton$: Observable<boolean> = this.isLargeScreen$.pipe(
    map(isLargeScreen => !isLargeScreen),
  );

  public displayedColumns$: Observable<Array<SupportedColumns>> = combineLatest(
    [
      this.isTableDisplay$,
      this.displayedColumnsInput$,
      this.sortColumnOnSmallScreenInput$,
      this.showRowMoreButton$,
    ],
  ).pipe(
    map(
      ([
        isTableDisplay,
        displayedColumns,
        sortColumnOnSmallScreen,
        showRowMoreButton,
      ]) => {
        let cols = [];
        if (isTableDisplay) {
          cols = displayedColumns;
        } else {
          cols = [sortColumnOnSmallScreen];
        }

        if (showRowMoreButton && isTableDisplay) {
          return [...cols, 'more'];
        }
        return cols;
      },
    ),
  );

  public isOnSmeListPage$ = this.routeLocatorService.isOnSmeListPage$;

  constructor(
    private readonly breakpointObserver: DacklBreakpointObserver,
    private readonly routeLocatorService: RouteLocatorService,
    public platform: Platform,
  ) {}

  public ngAfterViewInit(): void {
    // Keep the roving tabindex on a valid row when the rendered rows change
    // (e.g. after sorting or loading more entries).
    this.rowFocusElements.changes
      .pipe(takeUntil(this.destroySubj.asObservable()))
      .subscribe(() => {
        this.activeRowIndex = Math.min(
          this.activeRowIndex,
          Math.max(this.rowCount - 1, 0),
        );
      });
  }

  public ngOnDestroy(): void {
    this.destroySubj.next(true);
    this.destroySubj.complete();
  }

  public get rowCount(): number {
    return this.rowFocusElements?.length ?? 0;
  }

  /**
   * Moves keyboard focus to the row at the given index using a roving
   * tabindex. Out-of-bounds indices are ignored (no wrapping).
   */
  public focusRow(index: number, event?: Event): void {
    event?.preventDefault();

    if (index < 0 || index >= this.rowCount) {
      return;
    }

    this.activeRowIndex = index;
    this.getRowFocusElementsInDomOrder()[index]?.focus();
  }

  /**
   * Returns the focusable row elements in their visual (DOM) order.
   * The QueryList order is not reliable after sorting: mat-table moves the
   * row views and Angular then reports them in insertion order instead of
   * DOM order, which breaks index-based arrow key navigation.
   */
  private getRowFocusElementsInDomOrder(): Array<HTMLElement> {
    const elements =
      this.rowFocusElements?.toArray().map(ref => ref.nativeElement) ?? [];

    return elements.sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1,
    );
  }

  public loadMore(): void {
    this.onLoadMoreButtonClick.emit();
  }

  public retryOnError(): void {
    this.onErrorReloadButtonClick.emit();
  }

  public getDownloadNameId(index: number, collapsed: boolean = false): string {
    return `${collapsed ? 'collapsed-' : ''}download-name-${this.instanceId}-${index}`;
  }

  public getOverlayButtonLabelledBy(index: number, collapsed: boolean = false): string {
    return this.getDownloadNameId(index, collapsed);
  }

  public isProduct(download: Download): boolean {
    return isProduct(download);
  }
}
